import React, { useEffect, useRef } from 'react';
import { Send, X } from 'lucide-react';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import { useTranslation } from 'react-i18next';

const MAX_LENGTH = 2000;

const ChatInputBar = ({
    value,
    onChange,
    onSend,
    sending,
    isEditing,
    onCancelEdit,
}) => {
    const [t] = useTranslation('global');
    const textareaRef = useRef(null);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        const maxHeight = 24 * 4;
        textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [value]);

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isEditing) {
            event.preventDefault();
            onCancelEdit();
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (value.trim()) {
                onSend();
            }
        }
    };

    return (
        <div className="flex flex-col gap-2.5 border-t border-primary/10 bg-white px-3 py-2.5">
            {isEditing && (
                <div className="rounded-sm border border-information bg-information20 px-firstDegree py-1 flex items-center justify-between">
                    <Label size="small" message="management.onboarding.chat.editingBanner" />
                    <Button
                        style="ghost"
                        sizePadding="none"
                        showText={false}
                        showLeftIcon
                        leftIcon={<X className="h-4 w-4" />}
                        className="h-6 w-6 rounded-sm text-greyStrong hover:text-night"
                        onClick={onCancelEdit}
                        aria-label={t('global.actions.cancel')}
                    />
                </div>
            )}

            <div className="flex items-stretch gap-2">
                <div className="relative flex w-full">
                    <textarea
                        ref={textareaRef}
                        className="min-h-[38px] w-full rounded-[10px] border border-primary/10 bg-[hsl(var(--medium))] px-3 py-2 text-[12px] text-night outline-none"
                        placeholder={t('management.onboarding.chat.newMessagePlaceholder')}
                        value={value}
                        onChange={(event) => onChange(event.target.value.slice(0, MAX_LENGTH))}
                        onKeyDown={handleKeyDown}
                        name="onboarding-chat-input"
                    />
                    <div className="absolute right-2 top-1">
                        <Label size="small" color={value.length >= 1800 ? 'warning' : 'grey'}>
                            {value.length}/{MAX_LENGTH}
                        </Label>
                    </div>
                </div>

                <Button
                    style="ghost"
                    sizePadding="none"
                    showText={false}
                    showLeftIcon
                    leftIcon={<Send className="h-4 w-4" />}
                    disabled={sending || !value.trim()}
                    onClick={onSend}
                    className="h-[38px] w-[38px] rounded-sm bg-primary text-white transition-opacity hover:bg-primary_hover disabled:opacity-40"
                    aria-label={isEditing ? t('management.onboarding.actions.saveMessage') : t('management.onboarding.actions.sendMessage')}
                />
            </div>
        </div>
    );
};

export default ChatInputBar;
